import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Shield, Clock, Gauge, Car, AlertTriangle, Check, X, FileText, Download, Sun, Moon } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { useTheme } from '@/contexts/ThemeContext';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${window.location.origin}/api`;

function FullReportPage({ user }) {
  const { reportId } = useParams();
  const navigate = useNavigate();
  const { theme, toggleTheme } = useTheme();
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user) { navigate('/'); return; }
    fetchReport();
  }, [reportId, user]);

  const fetchReport = async () => {
    try {
      const res = await axios.get(`${API}/reports/${reportId}`, { withCredentials: true });
      setReport(res.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load report');
      navigate('/account');
    } finally {
      setLoading(false);
    }
  };

  if (loading) return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-950 flex items-center justify-center transition-colors">
      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-yellow-500" />
    </div>
  );

  if (!report) return null;

  const d = report.report_data || {};
  const v = d.vehicle || {};
  const history = d.history_check || {};
  const mot = d.mot_history || {};
  const mileage = d.mileage || {};
  const specs = d.specs || {};
  const valuation = d.valuation || {};
  const details = d.vehicle_details || {};

  const StatusBadge = ({ value, good, label }) => (
    <div className={`flex items-center gap-2 p-3 rounded-lg ${value ? (good ? 'bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-400' : 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400') : 'bg-gray-100 dark:bg-gray-800 text-gray-400 dark:text-gray-500'}`}>
      {value ? (good ? <Check className="h-4 w-4" /> : <X className="h-4 w-4" />) : <span className="h-4 w-4">-</span>}
      <span className="text-sm">{label}</span>
    </div>
  );

  const Section = ({ title, icon: Icon, children }) => (
    <Card className="bg-white dark:bg-gray-900 border-gray-200 dark:border-gray-800 mb-4">
      <CardContent className="p-5">
        <div className="flex items-center gap-2 mb-4">
          {Icon && <Icon className="h-5 w-5 text-yellow-500" />}
          <h3 className="text-lg font-bold text-gray-900 dark:text-white">{title}</h3>
        </div>
        {children}
      </CardContent>
    </Card>
  );

  const DataRow = ({ label, value }) => (
    <div className="flex justify-between py-2 border-b border-gray-100 dark:border-gray-800 last:border-0">
      <span className="text-gray-500 dark:text-gray-400 text-sm">{label}</span>
      <span className="text-gray-900 dark:text-white text-sm font-medium">{value ?? 'N/A'}</span>
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-950 transition-colors">
      <header className="border-b border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-950 transition-colors">
        <div className="container mx-auto px-4 py-3 flex items-center justify-between">
          <Button variant="ghost" size="sm" onClick={() => navigate('/account')} className="text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white">
            <ArrowLeft className="h-4 w-4 mr-1" /> My Account
          </Button>
          <div className="flex items-center gap-3">
            <button
              onClick={toggleTheme}
              className="p-2 rounded-full bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors"
              data-testid="theme-toggle-btn"
              aria-label="Toggle theme"
            >
              {theme === 'dark' ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
            </button>
            <Button
              variant="outline"
              size="sm"
              className="border-yellow-500/50 text-yellow-600 dark:text-yellow-500 hover:bg-yellow-500/10"
              onClick={() => {
                window.open(`${API}/reports/${reportId}/pdf`, '_blank');
              }}
              data-testid="download-pdf-btn"
            >
              <Download className="h-4 w-4 mr-1" /> Download PDF
            </Button>
            <span className="text-gray-500 dark:text-gray-400 text-sm">{user?.name}</span>
          </div>
        </div>
      </header>

      <div className="container mx-auto px-4 py-8 max-w-3xl">
        {/* Header */}
        <div className="text-center mb-8">
          <p className="text-gray-400 dark:text-gray-500 text-sm mb-2">Full Vehicle History Report</p>
          <div className="inline-block bg-gradient-to-b from-yellow-300 to-yellow-500 px-8 py-3 rounded-lg border-4 border-gray-900 shadow-xl">
            <span className="font-black text-2xl text-gray-900 tracking-widest">{report.registration}</span>
          </div>
          <p className="text-gray-500 dark:text-gray-400 mt-2">{v.year_of_manufacture} {v.make} {v.model} — {v.colour}</p>
          <p className="text-gray-400 dark:text-gray-600 text-xs mt-1">Report expires {new Date(report.expires_at).toLocaleDateString('en-GB')}</p>
        </div>

        {/* Safety Checks */}
        <Section title="Safety Checks" icon={Shield}>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
            <StatusBadge value={history.stolen !== undefined} good={!history.stolen} label="Not Stolen" />
            <StatusBadge value={history.writeOff !== undefined} good={!history.writeOff} label="No Write-off" />
            <StatusBadge value={history.finance !== undefined} good={!history.finance} label="No Finance" />
            <StatusBadge value={history.scrapped !== undefined} good={!history.scrapped} label="Not Scrapped" />
            <StatusBadge value={history.imported !== undefined} good={true} label={history.imported ? 'Imported' : 'UK Origin'} />
            <StatusBadge value={history.exported !== undefined} good={!history.exported} label="Not Exported" />
          </div>
        </Section>

        {/* Vehicle Details */}
        <Section title="Vehicle Details" icon={Car}>
          <DataRow label="Make" value={v.make} />
          <DataRow label="Model" value={details?.model || v.model} />
          <DataRow label="Colour" value={v.colour} />
          <DataRow label="Year" value={v.year_of_manufacture} />
          <DataRow label="Engine" value={v.engine_capacity ? `${v.engine_capacity}cc` : null} />
          <DataRow label="Fuel Type" value={v.fuel_type} />
          <DataRow label="CO2" value={v.co2_emissions ? `${v.co2_emissions} g/km` : null} />
          <DataRow label="Tax Status" value={v.tax_status} />
          <DataRow label="Tax Due" value={v.tax_due_date} />
          <DataRow label="MOT Status" value={v.mot_status} />
          <DataRow label="MOT Expiry" value={v.mot_expiry_date} />
          <DataRow label="Annual Tax" value={v.annual_tax_cost ? `£${v.annual_tax_cost}` : null} />
          <DataRow label="First Registered" value={v.month_of_first_registration} />
          <DataRow label="Wheelplan" value={v.wheelplan} />
          <DataRow label="Type Approval" value={v.type_approval} />
          {details?.insuranceGroup && <DataRow label="Insurance Group" value={details.insuranceGroup} />}
          {details?.numberOfDoors && <DataRow label="Doors" value={details.numberOfDoors} />}
          {details?.numberOfSeats && <DataRow label="Seats" value={details.numberOfSeats} />}
          {details?.transmission && <DataRow label="Transmission" value={details.transmission} />}
        </Section>

        {/* Valuation */}
        {valuation && Object.keys(valuation).length > 0 && (
          <Section title="Vehicle Valuation" icon={FileText}>
            {valuation.dealerRetail && <DataRow label="Dealer Retail" value={`£${valuation.dealerRetail?.toLocaleString()}`} />}
            {valuation.privateValue && <DataRow label="Private Sale" value={`£${valuation.privateValue?.toLocaleString()}`} />}
            {valuation.tradeIn && <DataRow label="Trade-in" value={`£${valuation.tradeIn?.toLocaleString()}`} />}
            {valuation.auctionValue && <DataRow label="Auction" value={`£${valuation.auctionValue?.toLocaleString()}`} />}
          </Section>
        )}

        {/* MOT History */}
        {mot && (Array.isArray(mot) ? mot.length > 0 : mot.motTests) && (
          <Section title="MOT History" icon={Clock}>
            {(Array.isArray(mot) ? mot : mot.motTests || []).slice(0, 10).map((test, i) => (
              <div key={i} className={`p-3 rounded-lg mb-2 ${test.testResult === 'PASSED' ? 'bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 dark:border-emerald-800/30' : 'bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800/30'}`}>
                <div className="flex items-center justify-between mb-1">
                  <span className={`text-sm font-semibold ${test.testResult === 'PASSED' ? 'text-emerald-700 dark:text-emerald-400' : 'text-red-700 dark:text-red-400'}`}>
                    {test.testResult || test.result}
                  </span>
                  <span className="text-gray-400 dark:text-gray-500 text-xs">{test.completedDate || test.date}</span>
                </div>
                {test.odometerValue && <p className="text-gray-500 dark:text-gray-400 text-xs">{test.odometerValue?.toLocaleString()} miles</p>}
                {test.advisories && test.advisories.length > 0 && (
                  <div className="mt-2 space-y-1">
                    {test.advisories.map((adv, j) => (
                      <p key={j} className="text-yellow-600 dark:text-yellow-500/80 text-xs flex items-start gap-1">
                        <AlertTriangle className="h-3 w-3 mt-0.5 shrink-0" />
                        {typeof adv === 'string' ? adv : adv.text || adv.advisory}
                      </p>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </Section>
        )}

        {/* Mileage */}
        {mileage && (Array.isArray(mileage) ? mileage.length > 0 : mileage.mileageRecords) && (
          <Section title="Mileage History" icon={Gauge}>
            {(Array.isArray(mileage) ? mileage : mileage.mileageRecords || []).map((record, i) => (
              <DataRow key={i} label={record.date || record.dateOfInformation} value={`${(record.mileage || record.odometerValue)?.toLocaleString()} miles`} />
            ))}
          </Section>
        )}

        {/* Specs */}
        {specs && Object.keys(specs).length > 0 && (
          <Section title="Performance & Specs" icon={Gauge}>
            {specs.bhp && <DataRow label="BHP" value={specs.bhp} />}
            {specs.topSpeed && <DataRow label="Top Speed" value={`${specs.topSpeed} mph`} />}
            {specs.acceleration && <DataRow label="0-60 mph" value={`${specs.acceleration}s`} />}
            {specs.urbanMpg && <DataRow label="Urban MPG" value={specs.urbanMpg} />}
            {specs.extraUrbanMpg && <DataRow label="Extra-Urban MPG" value={specs.extraUrbanMpg} />}
            {specs.combinedMpg && <DataRow label="Combined MPG" value={specs.combinedMpg} />}
            {specs.kerbWeight && <DataRow label="Kerb Weight" value={`${specs.kerbWeight} kg`} />}
            {specs.length && <DataRow label="Length" value={`${specs.length} mm`} />}
            {specs.width && <DataRow label="Width" value={`${specs.width} mm`} />}
            {specs.height && <DataRow label="Height" value={`${specs.height} mm`} />}
          </Section>
        )}

        <div className="text-center mt-8">
          <Button onClick={() => navigate('/')} className="bg-yellow-500 hover:bg-yellow-400 text-gray-900 font-semibold">
            Check Another Vehicle
          </Button>
        </div>
      </div>
    </div>
  );
}

export default FullReportPage;
